import { useQuery } from '@tanstack/react-query';
import { listJobTitleRds } from 'services/Settings';
import { queryKeys } from 'hooks/config/queryClient';
import { toPracticeAreaParam } from 'hooks/queries/useCaseMattersPaginated';
/**
 * Normalise the raw axios response from `listJobTitleRds` into a stable shape.
 *
 * The RDS endpoint (`list-job-title-rds`) returns:
 *   {
 *     total_count: 26,
 *     data: [ { job_title_id, job_title, is_default, practice_areas: [{ id, name }], sk, pk, ... } ],
 *     page_count: 3,
 *     page: 1,
 *     limit: 10,
 *     has_next: true,
 *     has_previous: false
 *   }
 *
 * @param {*} raw — parsed response body (response.data)
 * @param {number} fallbackPage
 * @param {number} fallbackLimit
 */
function normalizeJobTitleRdsResponse(raw, fallbackPage, fallbackLimit) {
  const list = raw?.data ?? [];
  const rows = Array.isArray(list) ? list : [];
  const total_count = Number(raw?.total_count ?? 0) || 0;
  const page_count =
    Number(raw?.page_count) > 0 ? Number(raw.page_count) : fallbackLimit > 0 ? Math.max(1, Math.ceil(total_count / fallbackLimit)) : 1;

  return {
    rows,
    total_count,
    page_count,
    page: raw?.page ?? fallbackPage,
    limit: raw?.limit ?? fallbackLimit,
    has_next: Boolean(raw?.has_next),
    has_previous: Boolean(raw?.has_previous),
  };
}

/**
 * Invalidate all job-titles-rds queries (settings table + dropdown caches).
 *
 * @param {import('@tanstack/react-query').QueryClient} queryClient
 */
export const invalidateJobTitlesRds = (queryClient) =>
  queryClient.invalidateQueries({
    queryKey: queryKeys.jobTitlesRds.all,
    refetchType: 'active',
  });

const mapJobTitleRowToOption = (row) => {
  if (!row || typeof row !== 'object') return null;
  const id = row.job_title_id ?? row.value ?? row.sk;
  return {
    label: row.job_title,
    value: id,
    job_title: row.job_title,
    job_title_id: id,
    ...row,
  };
};

/**
 * Fetch one page of job titles for JobTitleSelect (AsyncPaginate + queryClient.fetchQuery).
 *
 * @param {Object} [params]
 * @param {number} [params.page=1]
 * @param {number} [params.pageSize=20]
 * @param {string} [params.sort_order='asc']
 * @param {string} [params.sort_by='job_title']
 * @param {string} [params.keyword=''] — sent as API `search_keyword`
 * @param {string|Array} [params.practiceAreaId='']
 * @param {boolean} [params.excludeFromLoading=true]
 */
export const fetchJobTitlesPage = async ({
  page = 1,
  pageSize = 20,
  sort_order = 'asc',
  sort_by = 'job_title',
  keyword = '',
  practiceAreaId = '',
  excludeFromLoading = true,
} = {}) => {
  const practice_area_id = toPracticeAreaParam(practiceAreaId) ?? '';
  const response = await listJobTitleRds({
    limit: pageSize,
    page,
    sort_order,
    sort_by,
    practice_area_id,
    keyword,
    excludeFromLoading,
  });
  const data = normalizeJobTitleRdsResponse(response?.data, page, pageSize);
  const options = data.rows.map(mapJobTitleRowToOption).filter(Boolean);

  return {
    options,
    hasMore: data.has_next,
    additional: { page },
  };
};

const extractJobTitleIdFromCreateResponse = (response) => {
  const payload = response?.data;
  if (!payload) return null;
  if (typeof payload === 'string') return null;

  if (payload.job_title_id) return payload.job_title_id;
  if (payload.id) return payload.id;
  if (payload.sk) return payload.sk;

  const message = payload.message;
  if (typeof message === 'object' && message !== null) {
    if (message.job_title_id) return message.job_title_id;
    if (message.id) return message.id;
    if (message.sk) return message.sk;
  }

  const nested = payload.data;
  if (nested?.job_title_id) return nested.job_title_id;
  if (nested?.id) return nested.id;
  if (nested?.sk) return nested.sk;

  const titles = payload.job_titles ?? nested?.job_titles;
  if (Array.isArray(titles) && titles[0]) {
    const first = titles[0];
    if (typeof first === 'object') return first.job_title_id ?? first.id ?? first.sk ?? null;
  }

  const list = Array.isArray(payload) ? payload : Array.isArray(nested) ? nested : null;
  if (list?.[0]?.job_title_id) return list[0].job_title_id;
  if (list?.[0]?.id) return list[0].id;
  if (list?.[0]?.sk) return list[0].sk;

  return null;
};

/** Resolve job title id from create API response only. */
export const resolveCreatedJobTitleId = (createResponse) => extractJobTitleIdFromCreateResponse(createResponse);

/**
 * Paginated job titles hook — `list-job-title-rds`.
 * Follows the same flat-key + inline-queryFn pattern as `useCaseMatterListV2Query`
 * so that React Query reliably detects page changes.
 *
 * @param {Object} [opts]
 * @param {number} [opts.limit=10]
 * @param {number} [opts.page=1]
 * @param {string} [opts.sort_order='asc'] — 'asc' | 'desc'
 * @param {string} [opts.sort_by='job_title']
 * @param {string} [opts.practice_area_id='']
 * @param {string} [opts.keyword=''] — sent as API `search_keyword`
 * @param {boolean} [opts.enabled=true]
 * @param {boolean} [opts.excludeFromLoading=false]
 */
export function useJobTitleListRdsQuery({
  limit = 10,
  page = 1,
  sort_order = 'asc',
  sort_by = 'job_title',
  practice_area_id = '',
  keyword = '',
  enabled = true,
  excludeFromLoading = false,
} = {}) {
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    // Flat array key — page is a direct element so React Query detects changes reliably.
    queryKey: queryKeys.jobTitlesRds.list(page, limit, sort_order, practice_area_id, keyword),
    queryFn: async () => {
      const response = await listJobTitleRds({
        limit,
        page,
        sort_order,
        sort_by,
        practice_area_id,
        keyword,
        excludeFromLoading,
      });
      return normalizeJobTitleRdsResponse(response?.data, page, limit);
    },
    enabled,
    keepPreviousData: true,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 2,
  });

  return {
    rows: data?.rows ?? [],
    totalCount: data?.total_count ?? 0,
    totalPages: data?.page_count ?? 1,
    hasNext: data?.has_next ?? false,
    hasPrevious: data?.has_previous ?? false,
    apiPage: data?.page ?? page,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  };
}
